import videojs from 'video.js';
const Component = videojs.getComponent('Component');
const MenuButton = videojs.getComponent('MenuButton');
import QualitySwitcherItem from './quality-switcher-item.js';

/**
 * The button component for toggling and selecting video qualities
 *
 * @param {Object} player   Main Player
 * @param {Object=} options Object of option names and values
 * @extends MenuButton
 */

const defaults = {
  show_icon: false
};

class QualitySwitcherButton extends MenuButton {
  constructor(player, options) {
    super(player, options);

    let options_ = videojs.mergeOptions(defaults, options);
    this.show_icon = options_.show_icon;
    this.remember_quality = options_.remember_quality

    this.createQualityEl(this.menuButton_.el());
    this.updateLabel();

    this.menuEl = this.el_.querySelector('.vjs-menu');
    this.initMenuPositionHandlers();

    const updateHandler = this.update.bind(this);
    const updateLabelHandler = this.updateLabel.bind(this);
    this.player_.quality_handler.qualities.addEventListener('change', updateHandler);
    this.player_.quality_handler.qualities.addEventListener('change_forced', updateHandler);
    this.player_.quality_handler.addEventListener('change', updateLabelHandler);

    let timeoutId;
    const resizeHandler = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (this.menuEl.classList.contains('vjs-lock-showing')) {
          this.setMenuPosition();
        }
      }, 50);
    };
    window.addEventListener('resize', resizeHandler);

    this.player_.on('dispose', () => {
      this.player_.quality_handler.qualities.removeEventListener('change', updateHandler);
      this.player_.quality_handler.removeEventListener('change', updateLabelHandler);
      window.removeEventListener('resize', resizeHandler);
      clearTimeout(timeoutId);
      this.menuElObserver.disconnect();
      this.switcherElObserver.disconnect();
    }); 
  }

  observeSwitcherEl() {
    this.switcherElObserver.observe(
      this.el(),
      {attributes: true, childList: false, subtree: false}
    );
  }

  observeMenuEl() {
    this.menuElObserver.observe(
      this.menuEl,
      {attributes: true, childList: false, subtree: false}
    );
  }

  setMenuPosition() {
    this.menuElObserver.disconnect();
    this.switcherElObserver.disconnect();
    this.menuEl.classList.add('vjs-menu_hidden');
    this.el_.classList.remove('vjs-quality-switcher_to-right');
    const playerRect = this.player_.el_.getBoundingClientRect();
    const playerRightBorder = playerRect.left + playerRect.width;
    const menuRect = this.menuEl.getBoundingClientRect();
    const menuRightBorder = menuRect.left + menuRect.width;
    this.el_.classList[menuRightBorder >= playerRightBorder ? 'add' : 'remove']('vjs-quality-switcher_to-right');
    this.menuEl.classList.remove('vjs-menu_hidden');
    this.observeMenuEl();
    this.observeSwitcherEl();
  }

  initMenuPositionHandlers() {
    this.menuElObserver = new MutationObserver((mutationsList) => {
      const lastMutationWithClass = [...mutationsList]
        .filter((mutation) => mutation.attributeName === 'class')
        .slice(-1)[0];
      if (lastMutationWithClass) {
        lastMutationWithClass.target.classList.contains('vjs-lock-showing')
          ? this.setMenuPosition() : this.el_.classList.remove('vjs-quality-switcher_to-right');
      }
    });
    this.observeMenuEl();

    this.switcherElObserver = new MutationObserver((mutationsList) => {
      const lastMutationWithClass = [...mutationsList]
        .filter((mutation) => mutation.attributeName === 'class')
        .slice(-1)[0];
      if (lastMutationWithClass) {
        if (lastMutationWithClass.target.classList.contains('vjs-hover')) {
          this.setMenuPosition();
        } else {
          this.switcherElObserver.disconnect();
          this.el_.classList.remove('vjs-quality-switcher_to-right');
          this.observeSwitcherEl();
        }
      }
    });
    this.observeSwitcherEl();
  }

  createQualityEl(el) {
    this.quality_el_ = videojs.dom.createEl('div', {
      className: 'vjs-quality-text'
    });
    el.appendChild(this.quality_el_);
  }

  buildWrapperCSSClass() {
    return `vjs-quality-switcher ${super.buildWrapperCSSClass()}`;
  }

  createEl() {
    return super.createEl('div');
  }

  update() {
    super.update();
    this.updateLabel();

    let supported = true;
    if (this.items && this.items.length >= 2 && supported) {
      this.show();
    } else {
      this.hide();
    }
  }

  createItems() {
    const items = [];
    for (let quality of this.player_.quality_handler.qualities) {
      items.push(new QualitySwitcherItem(this.player_, {
        quality: quality,
        qualities_list: this.player_.quality_handler.qualities,
        remember_quality: this.remember_quality
      }));
    }
    return items;
  }

  updateLabel() {
    if (this.quality_el_ && !this.show_icon) {
      const label = this.player_.quality_handler.current_quality.label;
      this.quality_el_.innerHTML = label;
      this.trigger({ type: 'updateLabel', label });
    }
  }
}

QualitySwitcherButton.prototype.controlText_ = 'Quality';

Component.registerComponent('QualitySwitcherButton', QualitySwitcherButton);
export default QualitySwitcherButton;
